/**
 * Report API Service
 * API calls for reports and analytics
 *
 * Story 2.1: Executive Summary Cards
 */

import { api } from './api';
import type { ApiResponse } from '../types';

// Types for report data
export interface DateRangeParams {
  startDate: string;
  endDate: string;
}

export interface ReportFilterParams {
  /** Department ids (uuids); a name is a 422. */
  departmentIds?: string[];
  /** Team ids (uuids); a name is a 422. */
  teamIds?: string[];
  ownerIds?: string[];
}

export interface FilterOption {
  id: string;
  name: string;
  count?: number;
}

/**
 * Item B / Change C2 — report scoping.
 *
 * A manager's reports cover OKRs owned by themselves plus their active direct
 * reports; an `account_admin` reads the whole tenant. `scope` tells the UI
 * which of those a figure represents — "Average progress 62%" is unreadable
 * without knowing whose.
 *
 * BOTH FIELDS ARE ABSENT for an unrestricted caller, which is what keeps an
 * `account_admin` response identical to its pre-Item-B shape. Never render a
 * scope label when `scope` is undefined.
 *
 * Mirrors `backend/src/utils/reportScope.ts`. NOTE: report types are duplicated
 * here rather than living in `shared/` — `shared/src/types` has never contained
 * any report type. If these two drift, the backend is the source of truth.
 */
export interface ReportScopeInfo {
  type: 'team' | 'organization';
  /** Size of the caller's scope: self + active direct reports. */
  ownerCount: number;
}

/**
 * Organisation-wide comparison figures for a scoped caller.
 *
 * Computed with NO client-supplied filters — it is strictly "this period, the
 * whole organisation" — so it must not be presented as responding to the
 * filter panel.
 */
export interface OKRHealthSummaryBaseline {
  totalOKRs: number;
  healthyCount: number;
  atRiskCount: number;
  criticalCount: number;
  stagnantCount: number;
  avgProgress: number;
  expectedProgress: number;
  completionPrediction: number;
}

export interface ProgressDistributionBaseline {
  mean: number;
  total: number;
}

export interface CompletionPredictionBaseline {
  predictedCompletionRate: number;
  currentCompletionRate: number;
}

export interface EngagementMetricsBaseline {
  activeUsers: number;
  checkInCompletionRate: number;
  averageCommentsPerOKR: number;
}

/**
 * Note the absent `byDepartment`: the baseline deliberately carries the
 * headline rate only. Returning the organisation's per-department breakdown
 * would restore exactly the cross-team detail the scoping removed.
 */
export interface CheckInComplianceBaseline {
  overallCompliance: number;
  onTime: number;
  late: number;
  missed: number;
}

export interface ReportFilterMetadata {
  departments: FilterOption[];
  teams: FilterOption[];
  owners: FilterOption[];
  /** Present only for a scoped caller; `owners` is then their team only. */
  scope?: ReportScopeInfo;
}

export interface ExecutiveSummary {
  totalOKRs: number;
  completionRate: number;
  averageProgress: number;
  atRiskCount: number;
  atRiskPercentage: number;
  totalOKRsChange?: number;
  completionRateChange?: number;
  averageProgressChange?: number;
}

export interface StatusDistributionItem {
  status: string;
  count: number;
  percentage: number;
}

export interface DepartmentProgress {
  /** Display label; "No department" for OKRs whose owner is in none. */
  department: string;
  /** Department uuid, or null for the "No department" row. */
  departmentId: string | null;
  okrCount: number;
  averageProgress: number;
  completionRate: number;
}

export interface EngagementMetrics {
  activeUsers: number;
  checkInCompletionRate: number;
  averageCommentsPerOKR: number;
  scope?: ReportScopeInfo;
  orgBaseline?: EngagementMetricsBaseline;
}

export type ActivityLevel = 'high' | 'medium' | 'low' | 'inactive';

export interface UserActivityItem {
  userId: string;
  firstName: string;
  lastName: string;
  /**
   * Present only when the caller is entitled to this user's address (own record,
   * account_admin/super_admin, or the caller's direct report). Phase 2B door 6.
   */
  email?: string;
  department: string | null;
  role: string;
  okrsOwned: number;
  checkInsSubmitted: number;
  commentsCount: number;
  lastLoginAt: string | null;
  activityLevel: ActivityLevel;
  activityScore: number;
}

export interface UserActivityResult {
  users: UserActivityItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  /** Rows endpoint — no `orgBaseline`; an org-wide row list is what scoping withholds. */
  scope?: ReportScopeInfo;
}

export interface UserActivityParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  activityLevel?: ActivityLevel;
}

export interface CheckInCompliance {
  overallCompliance: number;
  onTime: number;
  late: number;
  missed: number;
  /**
   * Grouped by the OKR owner's department memberships: a check-in counts in each of the
   * owner's departments, and `departmentId` is `null` only for the "No department" row.
   * For a scoped caller, only the departments their team spans.
   */
  byDepartment: Array<{ departmentId: string | null; department: string; compliance: number; total: number }>;
  scope?: ReportScopeInfo;
  orgBaseline?: CheckInComplianceBaseline;
}

/**
 * ⚠️ PARTIALLY scoped, by design. When `scope.type === 'team'`:
 *   - `topContributors` and `mostDiscussedOKRs` are the caller's TEAM only.
 *   - `totalComments` and `avgCommentsPerOKR` are still ORGANISATION-WIDE.
 *
 * The UI must label these two groups differently. There is no `orgBaseline`
 * here because the two unscoped fields already are the organisation figures.
 */
export interface CollaborationMetrics {
  totalComments: number;
  avgCommentsPerOKR: number;
  topContributors: Array<{ userId: string; name: string; count: number }>;
  mostDiscussedOKRs: Array<{ okrId: string; title: string; commentCount: number }>;
  scope?: ReportScopeInfo;
}

export interface FeatureUsageItem {
  feature: string;
  icon: 'check-ins' | 'comments' | 'alignments' | 'key-results' | 'objectives';
  usageCount: number;
  userCount: number;
  adoptionRate: number;
  trend: number;
}

export interface FeatureUsageData {
  features: FeatureUsageItem[];
  totalActiveFeatures: number;
  overallAdoptionRate: number;
  /**
   * ⚠️ A scoped caller receives `type: 'organization'` here, NOT `'team'`.
   * These figures really are tenant-wide — aggregate adoption only, and a
   * team-sized denominator would make every rate meaningless. The card must
   * therefore NOT inherit the page's team label. Same for ActiveUsersData.
   */
  scope?: ReportScopeInfo;
}

export interface ActiveUsersData {
  dau: number;
  wau: number;
  mau: number;
  dauTrend: number;
  wauTrend: number;
  mauTrend: number;
  dauWauRatio: number;
  wauMauRatio: number;
  totalUsers: number;
  activeRate: number;
  /**
   * ⚠️ `type: 'organization'` for a scoped caller — DAU/WAU/MAU are meaningless
   * at team size. See the note on FeatureUsageData.
   */
  scope?: ReportScopeInfo;
}

// OKR Health Report types (Phase 4)
export interface ProgressDistributionBucket {
  range: string;
  min: number;
  max: number;
  count: number;
  percentage: number;
}

export interface ProgressDistributionData {
  buckets: ProgressDistributionBucket[];
  stats: {
    mean: number;
    median: number;
    standardDeviation: number;
    total: number;
  };
  expectedProgress: number;
  okrsBehind: number;
  okrsAhead: number;
  scope?: ReportScopeInfo;
  orgBaseline?: ProgressDistributionBaseline;
}

export type RiskLevel = 'critical' | 'high' | 'medium';

export interface AtRiskOKRItem {
  okrId: string;
  title: string;
  ownerId: string;
  ownerName: string;
  /** Only present when the caller is entitled to this owner's address. */
  ownerEmail?: string;
  department: string | null;
  progress: number;
  expectedProgress: number;
  progressGap: number;
  daysSinceLastUpdate: number;
  riskLevel: RiskLevel;
  riskScore: number;
  riskFactors: string[];
  daysRemaining: number;
  status: string;
}

export interface AtRiskOKRsData {
  okrs: AtRiskOKRItem[];
  total: number;
  byCriticality: {
    critical: number;
    high: number;
    medium: number;
  };
  /** Rows endpoint — no `orgBaseline`; an org-wide row list is what scoping withholds. */
  scope?: ReportScopeInfo;
}

export interface StagnantOKRItem {
  okrId: string;
  title: string;
  ownerId: string;
  ownerName: string;
  /** Only present when the caller is entitled to this owner's address. */
  ownerEmail?: string;
  department: string | null;
  progress: number;
  daysSinceActivity: number;
  lastActivityDate: string | null;
  lastActivityType: string;
  status: string;
}

export interface StagnantOKRsData {
  okrs: StagnantOKRItem[];
  total: number;
  avgDaysSinceActivity: number;
  /** Rows endpoint — no `orgBaseline`. */
  scope?: ReportScopeInfo;
}

export type ConfidenceLevel = 'high' | 'medium' | 'low';
export type ImpactType = 'positive' | 'negative' | 'neutral';

export interface CompletionPredictionFactor {
  factor: string;
  impact: ImpactType;
  description: string;
}

export interface CompletionPredictionData {
  predictedCompletionRate: number;
  confidenceLevel: ConfidenceLevel;
  confidenceRange: { min: number; max: number };
  currentCompletionRate: number;
  projectedByStatus: {
    willComplete: number;
    atRisk: number;
    unlikely: number;
  };
  weekOverWeekTrend: number;
  factors: CompletionPredictionFactor[];
  scope?: ReportScopeInfo;
  orgBaseline?: CompletionPredictionBaseline;
}

export interface OKRHealthSummary {
  totalOKRs: number;
  healthyCount: number;
  atRiskCount: number;
  criticalCount: number;
  stagnantCount: number;
  avgProgress: number;
  expectedProgress: number;
  completionPrediction: number;
  scope?: ReportScopeInfo;
  orgBaseline?: OKRHealthSummaryBaseline;
}

// User Adoption Report types (Phase 5)
export interface ActivationFunnelStep {
  step: string;
  label: string;
  count: number;
  percentage: number;
  conversionRate: number;
  dropOffRate: number;
  medianTimeToReach: number | null;
}

export interface FunnelStuckUser {
  userId: string;
  name: string;
  email: string;
  daysSinceSignup: number;
}

export interface ActivationFunnelData {
  steps: ActivationFunnelStep[];
  totalUsers: number;
  fullyActivatedCount: number;
  fullyActivatedRate: number;
  usersStuckAtStep: Array<{
    step: string;
    users: FunnelStuckUser[];
  }>;
}

export interface TimeToFirstOKRDistribution {
  range: string;
  count: number;
  percentage: number;
}

export interface TimeToFirstOKRByRole {
  role: string;
  averageDays: number;
  count: number;
}

export interface UserWithoutOKR {
  userId: string;
  name: string;
  email: string;
  daysSinceActivation: number;
}

export interface TimeToFirstOKRData {
  averageDays: number;
  medianDays: number;
  distribution: TimeToFirstOKRDistribution[];
  usersWithoutOKR: UserWithoutOKR[];
  byRole: TimeToFirstOKRByRole[];
  trend: number;
}

export interface FeatureDiscoveryItem {
  feature: string;
  label: string;
  avgDaysToDiscover: number;
  usersDiscovered: number;
  discoveryRate: number;
  usersNotDiscovered: number;
}

export interface FeatureDiscoveryData {
  features: FeatureDiscoveryItem[];
  totalActiveUsers: number;
  usersWithAllFeatures: number;
  avgFeaturesPerUser: number;
}

export interface RetentionCohort {
  cohortMonth: string;
  usersInCohort: number;
  retention: Array<{
    period: number;
    activeUsers: number;
    retentionRate: number;
  }>;
}

export interface ChurnedUser {
  userId: string;
  name: string;
  email: string;
  lastLoginDaysAgo: number;
  role: string;
}

export interface RetentionMetricsData {
  day7Retention: number;
  day30Retention: number;
  day90Retention: number;
  cohorts: RetentionCohort[];
  churnedUsers: ChurnedUser[];
  atRiskUsers: ChurnedUser[];
  totalActiveUsers: number;
  totalChurnedUsers: number;
  churnRate: number;
}

export interface UserAdoptionSummary {
  totalUsers: number;
  activatedUsers: number;
  activationRate: number;
  avgTimeToFirstOKR: number;
  avgFeaturesUsed: number;
  day30Retention: number;
  churnRate: number;
  fullyOnboardedRate: number;
}

/**
 * Build query string from parameters
 */
function buildQueryString(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams
): string {
  const params = new URLSearchParams();
  params.append('startDate', dateRange.startDate);
  params.append('endDate', dateRange.endDate);

  if (filters?.departmentIds?.length) {
    filters.departmentIds.forEach((id) => params.append('departmentIds', id));
  }
  if (filters?.teamIds?.length) {
    filters.teamIds.forEach((id) => params.append('teamIds', id));
  }
  if (filters?.ownerIds?.length) {
    filters.ownerIds.forEach((id) => params.append('ownerIds', id));
  }

  return params.toString();
}

/**
 * Report API functions
 */
export const reportApi = {
  /**
   * Get filter options (departments, teams, owners)
   */
  getFilterOptions: async (): Promise<ReportFilterMetadata> => {
    const response = await api.get<ApiResponse<ReportFilterMetadata>>(
      '/reports/filter-options'
    );
    return response.data.data as ReportFilterMetadata;
  },

  /**
   * Get executive summary metrics
   */
  getExecutiveSummary: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<ExecutiveSummary> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<ExecutiveSummary>>(
      `/reports/executive/summary?${queryString}`
    );
    return response.data.data as ExecutiveSummary;
  },

  /**
   * Get OKR status distribution
   */
  getStatusDistribution: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<StatusDistributionItem[]> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<StatusDistributionItem[]>>(
      `/reports/executive/status-distribution?${queryString}`
    );
    return response.data.data as StatusDistributionItem[];
  },

  /**
   * Get progress by department
   */
  getProgressByDepartment: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<DepartmentProgress[]> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<DepartmentProgress[]>>(
      `/reports/executive/progress-by-department?${queryString}`
    );
    return response.data.data as DepartmentProgress[];
  },

  /**
   * Get engagement metrics
   */
  getEngagementMetrics: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<EngagementMetrics> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<EngagementMetrics>>(
      `/reports/engagement/metrics?${queryString}`
    );
    return response.data.data as EngagementMetrics;
  },

  /**
   * Get user activity breakdown
   */
  getUserActivity: async (
    dateRange: DateRangeParams,
    params?: UserActivityParams,
    filters?: ReportFilterParams
  ): Promise<UserActivityResult> => {
    // Dates and the department / team / owner filters, from the one shared builder.
    const searchParams = new URLSearchParams(buildQueryString(dateRange, filters));

    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.activityLevel) searchParams.append('activityLevel', params.activityLevel);

    const response = await api.get<ApiResponse<UserActivityResult>>(
      `/reports/engagement/user-activity?${searchParams.toString()}`
    );
    return response.data.data as UserActivityResult;
  },

  /**
   * Get check-in compliance metrics
   */
  getCheckInCompliance: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<CheckInCompliance> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<CheckInCompliance>>(
      `/reports/engagement/check-in-compliance?${queryString}`
    );
    return response.data.data as CheckInCompliance;
  },

  /**
   * Get collaboration metrics
   */
  getCollaborationMetrics: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<CollaborationMetrics> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<CollaborationMetrics>>(
      `/reports/engagement/collaboration?${queryString}`
    );
    return response.data.data as CollaborationMetrics;
  },

  /**
   * Get feature usage statistics
   */
  getFeatureUsage: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<FeatureUsageData> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<FeatureUsageData>>(
      `/reports/engagement/feature-usage?${queryString}`
    );
    return response.data.data as FeatureUsageData;
  },

  /**
   * Get active users metrics (DAU/WAU/MAU)
   */
  getActiveUsers: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<ActiveUsersData> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<ActiveUsersData>>(
      `/reports/engagement/active-users?${queryString}`
    );
    return response.data.data as ActiveUsersData;
  },

  // OKR Health Report endpoints (Phase 4)

  /**
   * Get OKR health summary
   */
  getOKRHealthSummary: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<OKRHealthSummary> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<OKRHealthSummary>>(
      `/reports/health/summary?${queryString}`
    );
    return response.data.data as OKRHealthSummary;
  },

  /**
   * Get progress distribution histogram
   */
  getProgressDistribution: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<ProgressDistributionData> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<ProgressDistributionData>>(
      `/reports/health/progress-distribution?${queryString}`
    );
    return response.data.data as ProgressDistributionData;
  },

  /**
   * Get at-risk OKRs
   */
  getAtRiskOKRs: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<AtRiskOKRsData> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<AtRiskOKRsData>>(
      `/reports/health/at-risk?${queryString}`
    );
    return response.data.data as AtRiskOKRsData;
  },

  /**
   * Get stagnant OKRs
   */
  getStagnantOKRs: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams,
    thresholdDays: number = 14
  ): Promise<StagnantOKRsData> => {
    const params = new URLSearchParams(buildQueryString(dateRange, filters));
    params.append('daysThreshold', thresholdDays.toString());

    const response = await api.get<ApiResponse<StagnantOKRsData>>(
      `/reports/health/stagnant?${params.toString()}`
    );
    return response.data.data as StagnantOKRsData;
  },

  /**
   * Get completion prediction
   */
  getCompletionPrediction: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<CompletionPredictionData> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<CompletionPredictionData>>(
      `/reports/health/completion-prediction?${queryString}`
    );
    return response.data.data as CompletionPredictionData;
  },

  // User Adoption Report endpoints (Phase 5)

  /**
   * Get user adoption summary
   */
  getUserAdoptionSummary: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<UserAdoptionSummary> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<UserAdoptionSummary>>(
      `/reports/adoption/summary?${queryString}`
    );
    return response.data.data as UserAdoptionSummary;
  },

  /**
   * Get activation funnel data
   */
  getActivationFunnel: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<ActivationFunnelData> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<ActivationFunnelData>>(
      `/reports/adoption/activation-funnel?${queryString}`
    );
    return response.data.data as ActivationFunnelData;
  },

  /**
   * Get time to first OKR metrics
   */
  getTimeToFirstOKR: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<TimeToFirstOKRData> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<TimeToFirstOKRData>>(
      `/reports/adoption/time-to-first-okr?${queryString}`
    );
    return response.data.data as TimeToFirstOKRData;
  },

  /**
   * Get feature discovery tracking data
   */
  getFeatureDiscovery: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<FeatureDiscoveryData> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<FeatureDiscoveryData>>(
      `/reports/adoption/feature-discovery?${queryString}`
    );
    return response.data.data as FeatureDiscoveryData;
  },

  /**
   * Get user retention metrics
   */
  getRetentionMetrics: async (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams
  ): Promise<RetentionMetricsData> => {
    const queryString = buildQueryString(dateRange, filters);
    const response = await api.get<ApiResponse<RetentionMetricsData>>(
      `/reports/adoption/retention-metrics?${queryString}`
    );
    return response.data.data as RetentionMetricsData;
  },
};

export default reportApi;
